import type {
  CatalogField,
  CatalogOption,
  CatalogProduct,
  CatalogSection,
  ProductCatalog,
} from '@/app/audience-products/services';
import { lineKey } from '@/lib/products';
import { GOAL_ANSWER_FIELD_KEYS, type ProductAnswers } from '@/stores/audience-store';
import type {
  AudienceProductSelection,
  ProductDetailChip,
  ProductDetailGroup,
} from '@/app/audience-overview/lib';
import { asArray, evalShowWhen, isEmptyAnswer } from './catalog-utils';
import { TARGETING_TYPE_SHORT_LABEL } from './group-labels';

const TARGETING_TYPE_FIELD_ID = 'targeting-type';
const DISP_PRODUCTS_FIELD_ID = 'disp-products';
const ED_TYPES_FIELD_ID = 'ed-product-types';
const ED_PUBS_FIELD_ID = 'ed-publications';

/** Parent→child grouped field pairs, folded as `Parent: Child` in recaps. */
const GROUPED_PAIRS: Record<string, string> = {
  [TARGETING_TYPE_FIELD_ID]: DISP_PRODUCTS_FIELD_ID,
  [ED_TYPES_FIELD_ID]: ED_PUBS_FIELD_ID,
};

const GOAL_FIELD_IDS = new Set(GOAL_ANSWER_FIELD_KEYS);

/** Sub-group titles for a product's catalog sections when the section omits its own label. */
const SECTION_LABEL_FALLBACK: Record<string, string> = {
  'lead-gen-group': 'Lead Gen',
  'display-group': 'Display',
  'email-nurture-group': 'Email Nurture',
};

/** Inline-metadata fields that carry a short prefix before their value
 *  (e.g. `Qualifying Question • Yes`, `Emails • 4`). */
const META_PREFIX_BY_FIELD: Record<string, string> = {
  'b-qualifying': 'Qualifying Question',
  'qualifying-qs': 'Qualifying Question',
  'c-email-count': 'Emails',
  'email-count': 'Emails',
  'email-goal': 'Goal',
};

/** Checklist fields whose items read clearer with a type prefix (e.g. `Product: …`). */
const ITEM_PREFIX_BY_FIELD: Record<string, string> = {
  'ed-product-types': 'Product',
  'ed-publications': 'Publication',
};

const isChecklist = (field: CatalogField): boolean =>
  field.kind === 'checklist' || field.kind === 'targeting-checklist';

/** Normalise a stored answer (array or scalar) to a list of option ids. */
const toIds = (raw: unknown): string[] => {
  if (Array.isArray(raw)) return raw as string[];
  if (typeof raw === 'string' && raw.length > 0) return [raw];
  return [];
};

const optionLabelMap = (field: CatalogField | undefined): Map<string, string> =>
  new Map((field?.options ?? []).map((o) => [o.id, o.label] as const));

/** Map a field's stored option ids → their catalog labels (fallback to the raw id). */
const optionLabels = (field: CatalogField, raw: unknown): string[] => {
  const byId = optionLabelMap(field);
  return toIds(raw).map((id) => byId.get(id) ?? id);
};

/** Sort selected ids by their position in the catalog option list (unknowns last),
 *  so the recap mirrors the configurator's display order rather than click order. */
const inCatalogOrder = (ids: string[], options: CatalogOption[] = []): string[] => {
  const index = new Map(options.map((o, i) => [o.id, i] as const));
  const rank = (id: string): number => index.get(id) ?? Number.MAX_SAFE_INTEGER;
  return [...ids].sort((a, b) => rank(a) - rank(b));
};

const formatMeta = (field: CatalogField, raw: unknown): string => {
  const value = optionLabels(field, raw).join(', ');
  const prefix = META_PREFIX_BY_FIELD[field.id];
  return prefix ? `${prefix} • ${value}` : value;
};

interface SectionMeta {
  /** Metadata strings keyed by the checklist option they belong to. */
  byOption: Record<string, string[]>;
  /** Field ids that were folded inline (so they aren't rendered as their own items). */
  fieldIds: Set<string>;
}

/**
 * A "metadata" field is a non-checklist field gated (via `showWhen`) on a
 * specific option of a checklist in the same section — e.g. the qualifying-question
 * and email-count fields that only appear for a chosen Lead Gen product. Their
 * values fold inline onto the triggering option instead of listing on their own.
 */
const collectSectionMeta = (section: CatalogSection, answers: ProductAnswers): SectionMeta => {
  const checklistIds = new Set(section.fields.filter(isChecklist).map((f) => f.id));
  const byOption: Record<string, string[]> = {};
  const fieldIds = new Set<string>();

  for (const field of section.fields) {
    if (isChecklist(field) || field.id === TARGETING_TYPE_FIELD_ID) continue;
    if (GOAL_FIELD_IDS.has(field.id)) continue;
    if (!field.showWhen || !checklistIds.has(field.showWhen.fieldId)) continue;
    if (!evalShowWhen(field, answers) || isEmptyAnswer(answers[field.id])) continue;

    fieldIds.add(field.id);
    const metaText = formatMeta(field, answers[field.id]);
    for (const optionId of field.showWhen.anyOf) {
      byOption[optionId] ??= [];
      byOption[optionId].push(metaText);
    }
  }
  return { byOption, fieldIds };
};

/**
 * A grouped parent→child pair → one item per selected child, prefixed with its
 * parent's label (`ABM: Custom Interactive Banners`, `Dedicated Newsletter: Ad Pulse`).
 * A parent with no selected children shows plain (`ABM`), no prefix.
 *
 * Selected children that belong to no parent group are rendered standalone at the
 * end: those are pre-grouping option generations (Editorial's flat `ed-pub-*` from
 * NPJB-459) which carry no parent, and historical `product_answers` is never
 * rewritten. Dropping them would silently blank a publication an audience really
 * has; inventing a parent for them would misattribute it.
 */
const groupedItems = (
  slug: string,
  section: CatalogSection,
  answers: ProductAnswers,
  parentFieldId: string,
  childFieldId: string,
): ProductDetailChip[] => {
  const parentField = section.fields.find((f) => f.id === parentFieldId);
  const childField = section.fields.find((f) => f.id === childFieldId);
  const childOptions = childField?.options ?? [];
  const parents = asArray(answers[parentFieldId]);
  const childSelectedSet = new Set(asArray(answers[childFieldId]));
  const parentLabels = optionLabelMap(parentField);
  const grouped = new Set<string>();

  const items: ProductDetailChip[] = [];
  for (const parentKey of inCatalogOrder(parents, parentField?.options)) {
    const short = TARGETING_TYPE_SHORT_LABEL[parentKey] ?? parentLabels.get(parentKey) ?? parentKey;
    // childOptions is already in catalog order; filter to this parent's selected children.
    const children = childOptions.filter(
      (o) => o.group === parentKey && childSelectedSet.has(o.id),
    );
    // Keyed like the backend's line: the child when one is picked, else the bare parent.
    if (children.length === 0) {
      items.push({ text: short, key: lineKey(slug, parentFieldId, parentKey) });
      continue;
    }
    for (const o of children) {
      items.push({ text: `${short}: ${o.label}`, key: lineKey(slug, childFieldId, o.id) });
      grouped.add(o.id);
    }
  }
  const orphans = childOptions.filter((o) => childSelectedSet.has(o.id) && !grouped.has(o.id));
  for (const o of orphans) items.push({ text: o.label, key: lineKey(slug, childFieldId, o.id) });
  return items;
};

/** One item per selected checklist option, with any inline metadata folded in. */
const checklistItems = (
  slug: string,
  field: CatalogField,
  raw: unknown,
  meta: SectionMeta,
): ProductDetailChip[] => {
  const byId = optionLabelMap(field);
  const prefix = ITEM_PREFIX_BY_FIELD[field.id];
  return inCatalogOrder(toIds(raw), field.options).map((id) => {
    const label = byId.get(id) ?? id;
    const chip: ProductDetailChip = {
      text: prefix ? `${prefix}: ${label}` : label,
      key: lineKey(slug, field.id, id),
    };
    const inline = meta.byOption[id];
    if (inline && inline.length > 0) chip.meta = inline;
    return chip;
  });
};

/** child field id → its grouping parent field id (inverse of `GROUPED_PAIRS`). */
const PARENT_BY_CHILD: Record<string, string> = Object.fromEntries(
  Object.entries(GROUPED_PAIRS).map(([parent, child]) => [child, parent]),
);

/** True when this section actually carries the grouped child field. */
const hasGroupedChild = (section: CatalogSection, childFieldId: string): boolean =>
  section.fields.some((f) => f.id === childFieldId);

/**
 * True when a grouped child is folded under a parent present in this SAME section, so it
 * must not also render on its own. The check is deliberately conditional: PGN carries
 * `disp-products` with no `targeting-type` since NPJB-613, and suppressing it there would
 * drop the whole Display group; suppressing it unconditionally in Display Media would
 * double-render every pick (`ABM: Banners` plus a bare `Banners`).
 */
const isFoldedChild = (section: CatalogSection, field: CatalogField): boolean => {
  const parentFieldId = PARENT_BY_CHILD[field.id];
  if (parentFieldId === undefined) return false;
  return section.fields.some((f) => f.id === parentFieldId);
};

/** True when a field contributes its own item(s) (not a goal, folded child, or inline-meta field). */
const isRenderableField = (
  section: CatalogSection,
  field: CatalogField,
  meta: SectionMeta,
  answers: ProductAnswers,
): boolean => {
  // Numeric fields are goals/metrics (lead/impression/newsletter-send goal) shown
  // in the Goals card, never as a bare item in the product list.
  if (field.kind === 'numeric') return false;
  if (GOAL_FIELD_IDS.has(field.id)) return false;
  // A grouped child is suppressed ONLY where its parent will carry it (see `isFoldedChild`).
  if (isFoldedChild(section, field)) return false;
  if (meta.fieldIds.has(field.id)) return false;
  return evalShowWhen(field, answers);
};

/** Resolve a single renderable field into its item chips. */
const fieldItems = (
  slug: string,
  section: CatalogSection,
  field: CatalogField,
  answers: ProductAnswers,
  meta: SectionMeta,
): ProductDetailChip[] => {
  const childFieldId = GROUPED_PAIRS[field.id];
  if (childFieldId !== undefined && hasGroupedChild(section, childFieldId)) {
    return groupedItems(slug, section, answers, field.id, childFieldId);
  }
  const raw = answers[field.id];
  if (isEmptyAnswer(raw)) return [];
  if (isChecklist(field)) return checklistItems(slug, field, raw, meta);
  const byId = optionLabelMap(field);
  return toIds(raw).map((id) => ({ text: byId.get(id) ?? id, key: lineKey(slug, field.id, id) }));
};

/** Resolve one catalog section into its list of selected-item chips. */
const resolveSection = (
  slug: string,
  section: CatalogSection,
  answers: ProductAnswers,
): ProductDetailChip[] => {
  const meta = collectSectionMeta(section, answers);
  const items: ProductDetailChip[] = [];
  for (const field of section.fields) {
    if (!isRenderableField(section, field, meta, answers)) continue;
    items.push(...fieldItems(slug, section, field, answers, meta));
  }
  return items;
};

/**
 * Turn a product's raw configurator answers into per-section item lists. Each
 * catalog section becomes one group (labelled only when the product has more than
 * one section — e.g. Programmatic Nurture's Lead Gen / Display / Email Nurture).
 * Goal fields ([[project_lead_metrics_naming]], shown in the Goals card) and the
 * grouped `disp-products` child (folded into its targeting-type parent) are omitted.
 */
export const resolveProductDetails = (
  catalogProduct: CatalogProduct,
  answers: ProductAnswers,
): ProductDetailGroup[] => {
  const multiSection = catalogProduct.sections.length > 1;
  const groups: ProductDetailGroup[] = [];
  for (const section of catalogProduct.sections) {
    const values = resolveSection(catalogProduct.id, section, answers);
    if (values.length === 0) continue;
    const label = multiSection ? (section.label ?? SECTION_LABEL_FALLBACK[section.id]) : undefined;
    groups.push({ label, values });
  }
  return groups;
};

/**
 * Attach resolved `details` to each selected product using the catalog. Returns
 * the input unchanged while the catalog query is still loading (catalog
 * undefined) or when a selection has no matching catalog entry.
 */
export const enrichSelected = (
  selected: AudienceProductSelection[],
  answersMap: Record<string, ProductAnswers>,
  catalog: ProductCatalog | undefined,
): AudienceProductSelection[] => {
  if (!catalog) return selected;
  const productById = new Map(catalog.products.map((p) => [p.id, p] as const));
  return selected.map((p) => {
    const catalogProduct = productById.get(p.id);
    if (!catalogProduct) return p;
    return { ...p, details: resolveProductDetails(catalogProduct, answersMap[p.id] ?? {}) };
  });
};
